import {
  AddMealInsightFeedbackRequest,
  MealInsightControllerService,
  MealInsightHistoryResponse
} from "@9amhealth/openapi";
import {
  DateFormatter,
  fromDate,
  getLocalTimeZone,
  parseDate,
  toCalendarDate,
  today as todayInZone
} from "@internationalized/date";
import { Cubit } from "blac-next";
import { globalEvents } from "src/constants/globalEvents";
import { getSupportedUserLocale } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { toast } from "src/state/state";

const PAGE_SIZE = 20;

const toLocalDay = (iso: string): string =>
  toCalendarDate(fromDate(new Date(iso), getLocalTimeZone())).toString();

const todayLocal = (): string => todayInZone(getLocalTimeZone()).toString();

const RATING_TO_FEEDBACK: Record<
  AddMealInsightFeedbackRequest.rating,
  MealInsightHistoryResponse.latestFeedback
> = {
  [AddMealInsightFeedbackRequest.rating.GOOD]:
    MealInsightHistoryResponse.latestFeedback.GOOD,
  [AddMealInsightFeedbackRequest.rating.BAD]:
    MealInsightHistoryResponse.latestFeedback.BAD,
  [AddMealInsightFeedbackRequest.rating.REMOVED]:
    MealInsightHistoryResponse.latestFeedback.REMOVED
};

/**
 * A day's worth of logged meals, ready to render as a sticky section in the
 * infinite-scroll list. Days are ordered most-recent-first; meals within a day
 * are ordered by time logged (earliest first) to match the design.
 */
export interface MealDayGroup {
  dateKey: string;
  label: string;
  meals: MealInsightHistoryResponse[];
}

const dayLabel = (dateKey: string, today: string): string => {
  if (dateKey === today) {
    return translate("mealInsights.meals.today");
  }
  if (dateKey === parseDate(today).subtract({ days: 1 }).toString()) {
    return translate("mealInsights.meals.yesterday");
  }
  const date = parseDate(dateKey);
  const isCurrentYear = date.year === parseDate(today).year;
  return new DateFormatter(getSupportedUserLocale(), {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(isCurrentYear ? {} : { year: "numeric" })
  }).format(date.toDate(getLocalTimeZone()));
};

const buildDayGroups = (
  meals: MealInsightHistoryResponse[]
): MealDayGroup[] => {
  const today = todayLocal();
  const byDay = new Map<string, MealInsightHistoryResponse[]>();

  meals.forEach((meal) => {
    const day = toLocalDay(meal.createdAt);
    byDay.set(day, [...(byDay.get(day) ?? []), meal]);
  });

  return [...byDay.keys()]
    .sort((a, b) => b.localeCompare(a))
    .map((dateKey) => ({
      dateKey,
      label: dayLabel(dateKey, today),
      meals: [...(byDay.get(dateKey) ?? [])].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt)
      )
    }));
};

/**
 * A summary of the meals logged today, used by the "Today" insights card footer
 * ("Updated at {time} · {n} meals logged"). `latestMealAt` is the ISO timestamp
 * of the most recent meal logged today, or undefined when nothing is logged yet.
 */
export interface TodaySummary {
  mealCount: number;
  latestMealAt?: string;
}

const computeTodaySummary = (
  meals: MealInsightHistoryResponse[]
): TodaySummary => {
  const today = todayLocal();
  const todayMeals = meals.filter(
    (meal) => toLocalDay(meal.createdAt) === today
  );
  const latestMealAt = todayMeals.reduce<string | undefined>(
    (latest, meal) =>
      !latest || meal.createdAt.localeCompare(latest) > 0
        ? meal.createdAt
        : latest,
    undefined
  );

  return { mealCount: todayMeals.length, latestMealAt };
};

export interface MealInsightsState {
  meals: MealInsightHistoryResponse[];
  dayGroups: MealDayGroup[];
  todaySummary: TodaySummary;
  hasAnyMeals: boolean;
  page: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  initialLoadComplete: boolean;
  error?: Error;
}

const buildInitialState = (): MealInsightsState => ({
  meals: [],
  dayGroups: [],
  todaySummary: { mealCount: 0, latestMealAt: undefined },
  hasAnyMeals: false,
  page: 0,
  hasMore: true,
  loading: false,
  loadingMore: false,
  initialLoadComplete: false
});

export default class MealInsightsCubit extends Cubit<MealInsightsState> {
  static keepAlive = true;

  constructor() {
    super(buildInitialState());

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit(buildInitialState());
    });
  }

  /**
   * Fetches one page of meal history. The endpoint returns insights most recent
   * first; we keep that order and group client-side for rendering.
   */
  private readonly fetchPage = async (
    page: number
  ): Promise<{
    content: MealInsightHistoryResponse[];
    hasMore: boolean;
  }> => {
    const response = await MealInsightControllerService.getMealInsightHistory(
      undefined,
      undefined,
      undefined,
      undefined,
      page,
      PAGE_SIZE
    );
    const content = response.data.content ?? [];
    const pagination = response.data.page;
    const hasMore = pagination
      ? pagination.number + 1 < pagination.totalPages
      : content.length === PAGE_SIZE;

    return { content, hasMore };
  };

  private readonly recompute = (): void => {
    this.patch({
      dayGroups: buildDayGroups(this.state.meals),
      todaySummary: computeTodaySummary(this.state.meals)
    });
  };

  /**
   * Appends a page of meals to the accumulated list, de-duplicating by id so an
   * overlapping page (e.g. a new meal logged mid-session shifting the window)
   * can't insert a row twice.
   */
  private readonly appendMeals = (
    incoming: MealInsightHistoryResponse[]
  ): void => {
    const seen = new Set(this.state.meals.map((meal) => meal.id));
    const merged = [
      ...this.state.meals,
      ...incoming.filter((meal) => !seen.has(meal.id))
    ];
    this.patch({ meals: merged });
    this.recompute();
  };

  readonly loadInitial = async (): Promise<void> => {
    if (this.state.loading) {
      return;
    }

    this.patch({ loading: true, error: undefined });

    try {
      const [pageResult, exists] = await Promise.all([
        this.fetchPage(0).catch((e: unknown) => {
          reportErrorSentry(e);
          return null;
        }),
        MealInsightControllerService.hasAnyMealInsights().catch(
          (e: unknown) => {
            reportErrorSentry(e);
            return null;
          }
        )
      ]);

      if (!pageResult) {
        // The page fetch failed (e.g. offline). Keep initialLoadComplete false
        // so the view retries on the next visit / reconnect instead of latching
        // an empty state.
        this.patch({ hasAnyMeals: exists?.data ?? this.state.hasAnyMeals });
        return;
      }

      this.patch({
        meals: pageResult.content,
        page: 0,
        hasMore: pageResult.hasMore,
        hasAnyMeals: exists?.data ?? pageResult.content.length > 0,
        initialLoadComplete: true
      });
      this.recompute();
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ error: e as Error });
    } finally {
      this.patch({ loading: false });
    }
  };

  readonly loadMore = async (): Promise<void> => {
    if (
      this.state.loadingMore ||
      !this.state.hasMore ||
      !this.state.initialLoadComplete
    ) {
      return;
    }

    const nextPage = this.state.page + 1;
    this.patch({ loadingMore: true, error: undefined });

    try {
      const { content, hasMore } = await this.fetchPage(nextPage);
      this.appendMeals(content);
      this.patch({ page: nextPage, hasMore });
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ error: e as Error });
    } finally {
      this.patch({ loadingMore: false });
    }
  };

  readonly invalidate = (): void => {
    this.patch({
      meals: [],
      dayGroups: [],
      page: 0,
      hasMore: true,
      initialLoadComplete: false
    });
  };

  readonly deleteMeal = async (mealInsightId: string): Promise<boolean> => {
    try {
      await MealInsightControllerService.deleteMealInsight(mealInsightId);

      this.patch({
        meals: this.state.meals.filter((meal) => meal.id !== mealInsightId)
      });
      this.recompute();
      return true;
    } catch (e) {
      toast.error("mealInsights.error");
      reportErrorSentry(e);
      return false;
    }
  };

  readonly rateMeal = async (
    mealInsightId: string,
    rating: AddMealInsightFeedbackRequest.rating
  ): Promise<void> => {
    try {
      await MealInsightControllerService.addMealInsightFeedback(mealInsightId, {
        rating
      });

      const latestFeedback = RATING_TO_FEEDBACK[rating];
      this.patch({
        meals: this.state.meals.map((meal) =>
          meal.id === mealInsightId ? { ...meal, latestFeedback } : meal
        )
      });
      this.recompute();
    } catch (e) {
      reportErrorSentry(e);
    }
  };
}
